import { DiscountParams } from 'models/Discounts'
import { PaymentTypeDetails } from 'models/PaymentTypeDetails'
import { Products } from 'models/Plans'
import { getDefaultDaysToReport } from 'templates/Charge/sharedCharge.utils'
import { v4 as uuidv4 } from 'uuid'

import { enumNature } from 'components/Modal/ModalInvoice/ModalInvoice.utils'
import { Tag } from 'components/TagsAddInput/TagsAddInput'

import formatCents from 'utils/format-cents'
import { FREQUENCY_NAME_TO_ENUM, presetToPair } from 'utils/frequency'
import { convertTagsStringToArray } from 'utils/tags'

import CreatePaymentLinkFormInterface, {
  CustomerGroups,
  PaymentLink,
  ProductParams
} from './interface'

// Interface para os dados retornados pela API v3
interface PaymentLinkV3Response {
  uuid: string
  status: string
  title: string | null
  description: string | null
  billing_description?: string | null
  message: string | null
  created_at: string
  updated_at: string
  open_count: number
  send_customer_welcome: boolean
  simplified_checkout?: boolean
  customer_groups_uuids: string
  custom_output_url: string | null
  enable_customer_select_quantity: boolean
  quantity_default: number
  payment_type: string
  interest_fee: string | null
  interest_fine: string | null
  interest_policy: string
  discount_policy: string
  nfse_policy: string
  limit_days_for_payment: number
  bonus_policy: string
  total_cycles: number
  charge_type: string
  due_day: number | null
  due_date: string | null
  frequency: string
  frequency_type?: 'days' | 'weeks' | 'months' | 'years'
  frequency_number?: number
  frequency_description?: string
  due_type: string
  document_type?: 'cpf' | 'cnpj' | 'both'
  prorated_charge: boolean
  cancel_if_first_billing_unpaid?: boolean
  amount: string
  link: string
  customer_groups: any[]
  extra_fields: any[]
  discounts: Array<{
    amount: string | number
    days: number
    policy: string
    uuid?: string
  }>
  // Dados de bonificação da API v3
  bonus?: {
    total_cycles: number
    start_cycle?: number
    amount: number
  }
  billing_items: Array<{
    uuid: string
    description: string
    amount_billed: string
    amount_unit: string
    quantity: string
    billable_type: string
    created_at: string
    updated_at: string
    enable_customer_select_quantity?: boolean
    minimum_quantity?: number
    product?: {
      uuid: string
      name: string
      description?: string
    }
  }>
  merchant: {
    name: string
    uuid: string
    email: string
  }
  customer_uuid: string | null
  merchant_uuid: string
  first_amount: string
  custom_fields: Array<Record<string, any>>
  billings: any[]
  recurrences: any[]
  metadata?: Record<string, any>
  tags?: string
  // Dados do customer se existir
  customer?: {
    uuid: string
    status: string
    name: string
    nickname?: string
    document?: string
    email?: string
    phone?: string
    created_at: string
    updated_at: string
    source: string
    email_block: number
    address?: {
      addressable_type: string
      street?: string
      number?: string
      complement?: string
      neighborhood?: string
      city?: string
      state?: string
      postal_code?: string
      country_code?: string
      address_type?: string
      uuid: string
      created_at: string
      updated_at: string
      status: string
    }
  }
  // Dados de parcelamento
  product_link_installment?: {
    uuid: string
    max_installments: number
    max_installments_without_fee: number
    created_at: string
    updated_at: string
  }
  // Nova estrutura de meios de pagamento
  payment_type_details?: PaymentTypeDetails
  // Campos adicionais para edição
  nfse_scheduled?: {
    iss_retention: boolean
    nfse_issuer_service?: {
      uuid: string
    }
    days?: string | number
    amount_type: string | number
    description: string
    copy_description: boolean
    nfse_installment_scope?: string
    nature_operation: string | number
    issue_when: string
    amount?: string | number
  }
  nfse_issuer_uuid?: string
  // Dados de negativação - estrutura específica para links de pagamento
  billing_no_payment_report_scheduled?: {
    uuid: string
    billing_no_payment_reportable_type: string
    created_at: string
    updated_at: string
    days_to_report: number | null
  }
  emails_to_notify?: string | null
  consider_active_on_registration?: boolean
}

/**
 * Mapeia dados da API v3 (/v1/product_links/:uuid) para o formulário de payment link
 * Foca apenas nos campos específicos do payment link - campos compartilhados são tratados pelo setSharedFormData
 */
export const createFormValuesFromPaymentLinkV3 = (
  paymentLinkV3: PaymentLinkV3Response,
  availableTags?: Tag[]
): CreatePaymentLinkFormInterface => {
  // A frequência é o preset (rótulo) mais o par canônico, que é o que de fato
  // define o intervalo — sem ele um link "Personalizada" reabre como Mensal.
  const [frequencyType, frequencyNumber] = presetToPair(
    paymentLinkV3.frequency,
    paymentLinkV3.frequency_type,
    paymentLinkV3.frequency_number
  )

  // ✅ Não inicializar discounts e identificadores internos aqui
  // Eles serão tratados pelo setSharedFormData após o reset do formulário

  // Mapear billing_items para items do formulário
  const items =
    paymentLinkV3.billing_items?.map((item) => {
      const mappedItem = {
        uuid: item.uuid,
        description: item.description || '',
        price: parseFloat(item.amount_unit) || 0,
        quantity: parseFloat(item.quantity) || 1,
        total: parseFloat(item.amount_billed) || 0,
        typedValue: '' as const,
        enable_customer_select_quantity:
          item.enable_customer_select_quantity || false,
        minimum_quantity: item.minimum_quantity ?? 1,
        product: item.product
          ? {
              label: item.product.name,
              value: item.product.uuid,
              total_cycles: (item.product as any).total_cycles
            }
          : undefined
      }

      return mappedItem
    }) || []

  // Mapear payment_type string para array
  const payments = paymentLinkV3.payment_type
    ? paymentLinkV3.payment_type.split(';').filter(Boolean)
    : []

  // Mapear total_cycles (se > 12, usar 13 para "Outros")
  const totalCycles =
    paymentLinkV3.total_cycles > 12 ? 13 : paymentLinkV3.total_cycles
  const totalCyclesCustom =
    paymentLinkV3.total_cycles > 12 ? paymentLinkV3.total_cycles.toString() : ''

  // Mapear customer se existir
  const customer = paymentLinkV3.customer_uuid
    ? {
        label: paymentLinkV3.customer?.name || '',
        value: paymentLinkV3.customer_uuid
      }
    : { label: '', value: '' }
  const isCustomPolicy =
    paymentLinkV3?.nfse_policy === 'on_before_due_date' ||
    paymentLinkV3?.nfse_policy === 'on_after_due_date'
  const data: CreatePaymentLinkFormInterface = {
    // Campos básicos do payment link
    title: paymentLinkV3.title || '',
    hasDescription: !!paymentLinkV3.description,
    description: paymentLinkV3.description || '',
    hasCustomMessage: !!paymentLinkV3.message,
    customMessage: paymentLinkV3.message || '',
    hasCustomUrl: !!paymentLinkV3.custom_output_url,
    customUrl: paymentLinkV3.custom_output_url || '',
    sendWelcomeEmail: paymentLinkV3.send_customer_welcome || false,
    simplifiedCheckout: paymentLinkV3.simplified_checkout || false,
    canChangeCheckoutQuantity:
      paymentLinkV3.enable_customer_select_quantity || false,
    defaultQuantity: paymentLinkV3.quantity_default || 1,

    // Campos de cliente
    customerType: paymentLinkV3.customer_uuid ? 'existing' : 'new',
    customer,
    customerUuid: paymentLinkV3.customer_uuid || '',

    // Campos de grupos de clientes
    hasCustomerGroups: !!paymentLinkV3.customer_groups_uuids,
    groupsValue: [], // Será preenchido separadamente se necessário

    // Campos extras
    hasExtraFields: !!(
      (paymentLinkV3 as any).custom_field_configs?.length ||
      (paymentLinkV3 as any).custom_fields?.length ||
      paymentLinkV3.extra_fields?.length
    ),
    customDomain: '',
    hasCustomDomain: false,

    // Campos integrados do BodyModalNewChargeOrRecurrence
    items,
    hasItemsOnDescription: (paymentLinkV3.description || '').includes(
      '{{itens_da_cobranca}}'
    ),
    billingDescription: paymentLinkV3.billing_description || '',

    // ✅ Campos específicos do payment link (não compartilhados)
    productUuid: 'initial',
    group: { label: '', value: '' },
    groupUuid: '',
    recieverType: 'customer',

    // ✅ Campos do TypesCharges (específicos do payment link)
    chargeType: paymentLinkV3.charge_type === 'single' ? 0 : 1,
    dueType: paymentLinkV3.due_type || 'immediate_charge',
    proratedCharge: paymentLinkV3.prorated_charge || false,
    cancel_if_first_billing_unpaid:
      paymentLinkV3.cancel_if_first_billing_unpaid || false,

    // ✅ Campos compartilhados com valores padrão - serão sobrescritos pelo setSharedFormData
    frequency: FREQUENCY_NAME_TO_ENUM[paymentLinkV3.frequency] ?? 1,
    frequency_type: frequencyType,
    frequency_number: frequencyNumber,
    totalCycles,
    totalCyclesCustom,
    dueDay: paymentLinkV3.due_day?.toString() || undefined,
    dueDate: paymentLinkV3.due_date
      ? new Date(paymentLinkV3.due_date)
      : new Date(),
    payments,
    credit_card_uuid: { label: '', value: '' },
    max_installments:
      paymentLinkV3.product_link_installment?.max_installments ?? 12,
    // `??` (e não `||`) porque 0 é um valor válido aqui: significa "repassar
    // juros em todas as parcelas". Com `||`, um 0/null virava 12 e estourava a
    // validação `lte-max-installments` do sharedCharge.schema assim que a tela
    // abria, quando max_installments era menor que 12.
    max_installments_without_fee:
      paymentLinkV3.product_link_installment?.max_installments_without_fee ??
      null,

    // Configurações de juros
    interestPolicy: paymentLinkV3.interest_policy !== 'no_interest',
    interestFee: paymentLinkV3.interest_fee || '1.0',
    interestFine: paymentLinkV3.interest_fine || '2.0',

    // Configurações de desconto
    discountPolicy: paymentLinkV3.discount_policy !== 'no_discount',

    // Configurações de expiração
    expirationField: paymentLinkV3.limit_days_for_payment?.toString() || '60',
    limitDaysForPayment:
      paymentLinkV3.limit_days_for_payment?.toString() || '60',

    // Configurações de nota fiscal
    // A política manda: link com nota desligada (no_nfse) pode ter um
    // nfse_scheduled remanescente, e ligar o switch pela presença dele
    // reativava a emissão no próximo salvar.
    interestInvoice:
      !!paymentLinkV3.nfse_scheduled && paymentLinkV3.nfse_policy !== 'no_nfse',
    copy_description: paymentLinkV3.nfse_scheduled?.copy_description || false,
    iss_retention: paymentLinkV3.nfse_scheduled?.iss_retention ? 'yes' : 'no',
    service_list_code:
      paymentLinkV3.nfse_scheduled?.nfse_issuer_service?.uuid || '',
    // Mapear amount_type - a API V3 retorna string diretamente
    amount_type:
      (paymentLinkV3.nfse_scheduled?.amount_type as string) || 'amount_paid',
    descriptionInvoice: paymentLinkV3.nfse_scheduled?.description || '',
    nfse_issuer_uuid: paymentLinkV3.nfse_issuer_uuid || '',
    nfse_installment_scope:
      paymentLinkV3.nfse_scheduled?.nfse_installment_scope || 'per_cycle',
    // Se amount_type for custom, mapear o valor
    amountCustom:
      paymentLinkV3.nfse_scheduled?.amount_type === 'amount_custom' &&
      paymentLinkV3.nfse_scheduled?.amount
        ? formatCents(paymentLinkV3.nfse_scheduled.amount.toString())
        : '',
    nature_operation:
      (paymentLinkV3.nfse_scheduled?.nature_operation &&
        enumNature[
          paymentLinkV3.nfse_scheduled
            .nature_operation as keyof typeof enumNature
        ]) ||
      '1',
    issue_when: isCustomPolicy
      ? 'on_custom_issued'
      : paymentLinkV3.nfse_policy ||
        paymentLinkV3.nfse_scheduled?.issue_when ||
        'on_billing_issued',
    ...(isCustomPolicy && {
      days: paymentLinkV3?.nfse_scheduled?.days,
      due_date_period: paymentLinkV3.nfse_policy
    }),
    // Configurações de negativação - mapear da API (estrutura específica para links)
    interestNegativation:
      !!paymentLinkV3.billing_no_payment_report_scheduled?.days_to_report,
    daysToReport:
      paymentLinkV3.billing_no_payment_report_scheduled?.days_to_report?.toString() ||
      getDefaultDaysToReport(),

    // Configurações de bonificação
    hasBonus:
      paymentLinkV3.bonus_policy !== 'no_bonus' && !!paymentLinkV3.bonus,
    bonusSettings:
      paymentLinkV3.bonus_policy !== 'no_bonus' && paymentLinkV3.bonus
        ? {
            cyclesCount:
              paymentLinkV3.bonus.total_cycles > 12
                ? 13
                : paymentLinkV3.bonus.total_cycles || 1,
            cyclesCountCustom:
              (paymentLinkV3.bonus.total_cycles || 0) > 12
                ? paymentLinkV3.bonus.total_cycles.toString()
                : '',
            // Sem isto o formulário reabre sempre no ciclo 1, o que muda o teto de
            // validação (maxBonusCycles = totalCycles - startCycle + 1) e regrava
            // start_cycle: 1 por cima do que estava salvo.
            startCycle: paymentLinkV3.bonus.start_cycle || 1,
            // Mapear bonus_policy corretamente baseado no valor real
            type:
              paymentLinkV3.bonus_policy === 'fixed' ? 'value' : 'percentage',
            amount: paymentLinkV3.bonus.amount
              ? paymentLinkV3.bonus_policy === 'fixed'
                ? formatCents(paymentLinkV3.bonus.amount.toString())
                : paymentLinkV3.bonus.amount.toString()
              : ''
          }
        : {
            cyclesCount: 1,
            cyclesCountCustom: '',
            startCycle: 1,
            type: 'value',
            amount: ''
          },

    // Mapear tags
    tags:
      availableTags && paymentLinkV3.tags
        ? convertTagsStringToArray(paymentLinkV3.tags, availableTags)
        : [],

    // Mapear emails para notificar
    emails_to_notify: paymentLinkV3.emails_to_notify
      ? paymentLinkV3.emails_to_notify
          .split(';')
          .filter((email: string) => email.trim() !== '')
          .map((email: string) => ({ email: email.trim() }))
      : [],

    // Person Type (Tipo de Cliente) - mapear de backend para frontend
    link_type: paymentLinkV3.document_type
      ? paymentLinkV3.document_type === 'cpf'
        ? 'PF'
        : paymentLinkV3.document_type === 'cnpj'
        ? 'PJ'
        : 'BOTH'
      : 'BOTH',

    // Contacts (Contatos)
    contacts_enabled: !!(paymentLinkV3 as any).link_fields?.contacts?.enabled,
    contacts:
      (paymentLinkV3 as any).link_fields?.contacts?.enabled &&
      (paymentLinkV3 as any).link_fields?.contacts?.contacts_fields
        ? (paymentLinkV3 as any).link_fields.contacts.contacts_fields.map(
            (contact: any) => ({
              type: contact.contact_type || '',
              required: contact.required || false
            })
          )
        : [],

    // Existing document behavior (Comportamento no caso de cliente existente)
    existing_document_behavior: (() => {
      const behavior = (paymentLinkV3 as any).existing_document_behavior
      if (typeof behavior === 'number') return behavior
      if (behavior === 'skip_customer') return 0
      if (behavior === 'create_customer') return 1
      if (behavior === 'update_customer') return 2
      // SC-10380: fallback é update_customer (2)
      return 2
    })(),

    consider_active_on_registration:
      !paymentLinkV3.consider_active_on_registration
  }

  return data
}

export const createExtraFieldsInitialValue = (paymentLink: PaymentLink) => {
  // Se tem custom_field_configs (dados da API V3), usar esses dados
  if ((paymentLink as any).custom_field_configs) {
    return (paymentLink as any).custom_field_configs.map((config: any) => ({
      uuid: config.custom_field.uuid,
      name:
        config.custom_field.field_configuration?.title ||
        config.custom_field.title ||
        '',
      required: config.required || false,
      field_type: config.custom_field.field_type,
      options: config.custom_field.field_configuration?.options,
      field_configuration: config.custom_field.field_configuration,
      upload_settings: config.custom_field.upload_settings,
      // SC-11487: seção, condição e cópia do contratante no checkout
      display_settings: config.display_settings || undefined
    }))
  }

  // Se tem custom_fields (dados da API), usar esses dados
  if ((paymentLink as any).custom_fields) {
    return (paymentLink as any).custom_fields.map((field: any) => ({
      uuid: field.uuid,
      name: field.field_configuration?.title || field.title || field.name || '',
      required: field.required || false,
      field_type: field.field_type,
      field_configuration: field.field_configuration,
      upload_settings: field.upload_settings
    }))
  }

  // Fallback para extra_fields (formato antigo)
  return (
    paymentLink.extra_fields?.map((field) => ({
      uuid: uuidv4(),
      name: field.name || '',
      required: field.required || false,
      field_type: 'short_text' // ExtraFieldFromAPI não tem field_type, usar padrão
    })) || []
  )
}

export const createCustomerGroupsInitialValue = (paymentLink: PaymentLink) => {
  return paymentLink.customer_groups?.map(({ uuid, name }) => ({
    value: uuid,
    label: name
  })) as CustomerGroups[]
}

export const convertProductPropertiesToCamelCase = (
  product: ProductParams | undefined
): Products => {
  if (!product) {
    return {} as Products
  }
  return {
    chargeType: product.charge_type,
    dueType: product.due_type,
    proratedCharge: product.prorated_charge,
    amount: product.amount,
    createdAt: product.created_at,
    description: product.description,
    discountPolicy: product.discount_policy,
    dueDay: product.due_day,
    frequency: product.frequency,
    icon: product.icon,
    interestFee: product.interest_fee,
    interestFine: product.interest_fine,
    interestPolicy: product.interest_policy,
    merchantUuid: product.merchant_uuid,
    metadata: product?.metadata || undefined,
    bonus: product?.bonus || undefined,
    name: product.name,
    paymentType: product.payment_type?.split(';'),
    status: product.status,
    totalCycles: product.total_cycles,
    updatedAt: product.updated_at,
    uuid: product.uuid,
    billingDescription: product.billing_description || '',
    discounts: product.discounts ? (product.discounts as DiscountParams[]) : [],
    documentFile: product.document_file || undefined,
    productInstallment: {
      uuid: product.product_installment?.uuid || '',
      maxInstallments: product.product_installment?.max_installments || 12,
      maxInstallmentsWithoutFee:
        product.product_installment?.max_installments_without_fee || 12,
      createdAt: product.product_installment?.created_at || '',
      updatedAt: product.product_installment?.updated_at || ''
    }
  }
}
